import {AnchorButton, Button, Callout, Intent} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {Constants} from "../../constants/constants";

// The weekly reset is anchored to US Eastern (DP-3062). Always render it in
// Eastern regardless of the browser timezone, with the zone abbreviation —
// "EST" in winter, "EDT" during daylight saving — so it's unambiguous.
const RESET_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "America/New_York",
  timeZoneName: "short"
});

export interface RoutePlanningUsageInfo {
  used: number;
  limit: number;
  isUnlimited: boolean;
  resetDate: string;
}

interface RoutePlanningUsageBannerProps {
  usage?: RoutePlanningUsageInfo | null;
  open: boolean;
  onClose: () => void;
  // Bumped by the parent each time the user attempts a blocked action; a change
  // (when > 0) replays the attention pulse.
  attentionTick?: number;
}

// Horizontal padding mirrors the RoutePlanningView <Step> (16px), so the banner
// spans the same width as the in-step callouts (e.g. the optimization-objective
// warning). Pinned at the top of the content column.
const BannerWrapper = styled.div`
  position: relative;
  padding: 16px 16px 0;

  .bp5-callout {
    padding-right: 36px;
  }

  @keyframes rp-banner-pulse {
    0% {
      box-shadow: none;
    }
    50% {
      box-shadow: 0 0 0 3px rgba(217, 130, 43, 0.5);
    }
    100% {
      box-shadow: none;
    }
  }

  @keyframes rp-banner-shake {
    0%,
    100% {
      transform: translateX(0);
    }
    20% {
      transform: translateX(-4px);
    }
    40% {
      transform: translateX(4px);
    }
    60% {
      transform: translateX(-3px);
    }
    80% {
      transform: translateX(2px);
    }
  }

  .rp-attention {
    animation: rp-banner-pulse 1000ms ease-out 3, rp-banner-shake 450ms ease-in-out;
  }

  @media (prefers-reduced-motion: reduce) {
    .rp-attention {
      animation: none;
    }
  }
`;

const DismissButton = styled(Button)`
  position: absolute;
  top: 22px;
  right: 22px;
`;

const SubscriptionLink = () => (
  <AnchorButton
    data-testid="route-planning-subscription-link"
    href={Constants.ITINERARY_PLANNING_PREMIUM_CONSULT_URL}
    target="_blank"
    rel="noopener noreferrer"
    minimal
    small
    intent={Intent.PRIMARY}
  >
    Click here to learn more about Itinerary Planning upgrades
  </AnchorButton>
);

/**
 * Freemium usage banner (DP-3062). Rendered at the top of RoutePlanningView.
 * Visibility is controlled by the parent (`open`); the banner itself only
 * renders when there is a quota to show (a limit is set / not unlimited).
 */
const RoutePlanningUsageBanner = ({usage, open, onClose, attentionTick = 0}: RoutePlanningUsageBannerProps) => {
  if (!open || !usage || usage.isUnlimited) {
    return null;
  }

  const atLimit = usage.used >= usage.limit;
  // resetDate is an absolute instant (next 12:01 AM ET Monday); always shown in
  // Eastern (EST/EDT), independent of the browser timezone.
  const formattedReset = RESET_DATE_FORMATTER.format(new Date(usage.resetDate));

  return (
    <BannerWrapper>
      <Callout
        // Remount on each attention bump so the pulse animation replays. Only pulse
        // when actually out of plans (atLimit) — never on initial load, nor on a
        // later remount once the tenant is back under the limit.
        key={attentionTick}
        className={attentionTick > 0 && atLimit ? "rp-attention" : undefined}
        data-testid="route-planning-usage-banner"
        intent={atLimit ? Intent.WARNING : Intent.PRIMARY}
      >
        {atLimit ? (
          <>
            You&apos;re out of complimentary plans for this week! Your complimentary plans reset on {formattedReset}.{" "}
            <SubscriptionLink />
          </>
        ) : (
          <>
            You&apos;ve used {usage.used} of your {usage.limit} complimentary plans this week. <SubscriptionLink />
          </>
        )}
      </Callout>
      <DismissButton
        data-testid="route-planning-usage-banner-dismiss"
        aria-label="Dismiss"
        minimal
        small
        icon="cross"
        onClick={onClose}
      />
    </BannerWrapper>
  );
};

export default RoutePlanningUsageBanner;
